import { ComponentProps, ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDraggable } from '@dnd-kit/core';

import { SearchIcon } from '@/assets/icons';
import { Button, Card, Input } from '@/components';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SlidebarWrapper,
} from '@/components/organisms/Sidebar/Sidebar.tsx';
import { UUID } from '@/types/common';
import { cn } from '@/utils/tailwind.ts';

import { VisualisationValue } from '../EditCell/components/EditCellSidebar/constants';

type FilterTag = {
  type: BlockGroup | null;
  label: string;
};

export type BlockGroup = 'text' | 'numbers' | 'graphs';

export type BlockValue =
  | 'text-heading'
  | 'text-subtitle'
  | 'text-body-text'
  | 'numbers-avg-employee'
  | 'numbers-response-count'
  | 'numbers-participation-rate'
  | 'numbers-average-score'
  | 'numbers-enps'
  | 'graphs-avg-employee';

export type BlockItem = {
  id?: UUID;
  title: string;
  description: string;
  value: BlockValue;
  icon: ReactNode;
  editCellVariant: VisualisationValue;
  data?: Record<string, any>;
  size?: '1/3' | '2/3' | '3/3';
};

export type BlockGroupItem = {
  type: BlockGroup;
  items: BlockItem[];
};

type BlocksSidebarProps = {
  groups: BlockGroupItem[];
  sidebarProps?: Omit<ComponentProps<typeof Sidebar>, 'children'>;
};

type DraggableBlockProps = {
  id: string;
  data: any;
  children: React.ReactNode;
};

const DraggableBlock = ({ id, data, children }: DraggableBlockProps) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data,
  });

  return (
    <div ref={setNodeRef} {...listeners} {...attributes} className="w-full">
      {isDragging ? (
        <div className="w-full h-[80px] bg-custom-warm-grey-100 border border-custom-warm-grey-200 border-dashed rounded-[12px]" />
      ) : (
        children
      )}
    </div>
  );
};

export const BlocksSidebar = ({ groups, sidebarProps }: BlocksSidebarProps) => {
  const { t } = useTranslation('components');

  const [activeFilter, setActiveFilter] = useState<BlockGroup | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const filteredGroups = useMemo(() => {
    const result =
      activeFilter === null ? groups : groups.filter(group => group.type === activeFilter);

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      return result
        .map(group => ({
          ...group,
          items: group.items.filter(item => item.title.toLowerCase().includes(query)),
        }))
        .filter(group => group.items.length > 0);
    }

    return result;
  }, [activeFilter, groups, searchQuery]);

  const filterTags: FilterTag[] = useMemo(
    () => [
      {
        type: null,
        label: t('blocksSidebar.filters.all'),
      },
      {
        type: 'text',
        label: t('blocksSidebar.filters.text'),
      },
      {
        type: 'numbers',
        label: t('blocksSidebar.filters.numbers'),
      },
      {
        type: 'graphs',
        label: t('blocksSidebar.filters.graphs'),
      },
    ],
    [t]
  );

  return (
    <SidebarProvider>
      <SlidebarWrapper>
        <Sidebar {...sidebarProps} className="w-auto">
          <SidebarHeader className="p-0 gap-3 py-5 pt-6 px-4">
            <Input
              placeholder={t('blocksSidebar.placeholder')}
              leftIcon={<SearchIcon />}
              className="w-full"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <div className="flex items-center gap-2">
              {filterTags.map(tag => (
                <Button
                  size="xs"
                  variant="secondary"
                  key={tag.type}
                  onClick={() => setActiveFilter(tag.type)}
                  className={cn(
                    activeFilter === tag.type &&
                      'bg-custom-warm-grey-100 outline-custom-warm-grey-400'
                  )}
                >
                  {tag.label}
                </Button>
              ))}
            </div>
          </SidebarHeader>
          <SidebarContent className="gap-5 pb-4 overflow-x-hidden">
            {filteredGroups.map(group => (
              <SidebarGroup key={group.type} className="px-4 py-0 gap-3">
                <SidebarGroupLabel className="px-3 text-xs font-semibold text-custom-warm-grey-400 h-auto">
                  {filterTags.find(tag => tag.type === group.type)?.label}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu className="gap-3">
                    {group.items.map(item => (
                      <SidebarMenuItem key={item.value} className="group/menuitem">
                        <DraggableBlock id={item.value} data={item}>
                          <SidebarMenuButton asChild>
                            <Card className="gap-3 items-start p-3 h-full">
                              <div className="bg-custom-warm-grey-100 p-2.5 rounded-[8px] group-hover/menuitem:bg-card transition-colors">
                                {item.icon}
                              </div>
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-semibold">{item.title}</span>
                                <span className="text-xs text-custom-warm-grey-500">
                                  {item.description}
                                </span>
                              </div>
                            </Card>
                          </SidebarMenuButton>
                        </DraggableBlock>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>
        </Sidebar>
      </SlidebarWrapper>
    </SidebarProvider>
  );
};
